import React, { useState } from 'react';
import { CloseUniversal } from '@duxton-web/icons';
import { Alert, Button, Modal, Typography } from '@duxton-web/ui';
import { AggregateAjvError } from '@segment/ajv-human-errors';
import addFormats from 'ajv-formats';
import Ajv2019 from 'ajv/dist/2019';
import draft7MetaSchema from 'ajv/dist/refs/json-schema-draft-07.json';
import { produce } from 'immer';
import { parse } from 'lossless-json';

import FileUpload from '@/pages/ExperimentFormV2/common/FileUpload';
import JsonEditor from '@/pages/ExperimentFormV2/common/JsonEditor';
import { tryBeautifyJson } from '@/utils/json';
import { removeLosslessObject } from '@/utils/object';

interface EditJsonModalProps {
  data?: string;
  jsonSchema?: string;
  onOk?: (jsString: string) => void;
  onCancel?: () => void;
  readOnly?: boolean;
}

const ErrorList = ({ errors }: { errors: string[] }) => {
  return (
    <ul>
      {errors.map((e, idx) => (
        <li key={idx}>
          <Typography.Text>{e}</Typography.Text>
        </li>
      ))}
    </ul>
  );
};

const JsonEditorModalTitle = ({
  readOnly = false,
  onCancel,
}: {
  readOnly: boolean;
  onCancel: () => void;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Typography.Title level={3}>
        {readOnly ? 'View JSON' : 'Edit JSON'}
      </Typography.Title>
      <Button
        data-testid="json-editor-modal-close-button"
        type="default"
        shape="circle"
        ghost
        size="small"
        onClick={() => {
          onCancel();
        }}
        icon={<CloseUniversal />}
      />
    </div>
  );
};

const JsonEditorModal = ({
  onOk,
  onCancel,
  data = '',
  jsonSchema = '',
  readOnly = false,
}: EditJsonModalProps) => {
  const ajv = new Ajv2019({ allErrors: true, verbose: true });
  addFormats(ajv);
  ajv.addMetaSchema(draft7MetaSchema); // Support draft-07 schemas in addition to draft-2019-09

  const [jsonString, setJsonString] = useState(data);
  const [errors, setErrors] = useState<string[]>([]);

  return (
    <Modal
      centered
      title={<JsonEditorModalTitle onCancel={onCancel} readOnly={readOnly} />}
      onCancel={onCancel}
      width={700}
      open
      closable={false}
      maskClosable={false}
      mask
      // onCancel={onCancel}
      // onOk={onOk}
      footer={
        !readOnly && (
          <div style={{ display: 'flex', gap: 4 }}>
            <FileUpload
              mode="json"
              accept=".json"
              onChange={(jsString) => setJsonString(jsString)}
            >
              <Button
                onClick={(e) => {
                  e.preventDefault();
                }}
                size="small"
              >
                Upload from .json file
              </Button>
            </FileUpload>
            <Button
              onClick={(e) => {
                e.preventDefault();
                const beautified = tryBeautifyJson(jsonString);
                setJsonString(beautified);
              }}
              size="small"
            >
              Beautify JSON
            </Button>
            <Button
              type="primary"
              style={{ marginLeft: 'auto' }}
              onClick={(event) => {
                try {
                  event.preventDefault();

                  if (!jsonSchema) {
                    onOk(jsonString);
                    return;
                  }

                  const parseJsonSchema = parse(jsonSchema);
                  const parseJsonString = parse(jsonString);
                  const nextJsonSchema = produce(parseJsonSchema, (draft) => {
                    removeLosslessObject(draft);
                  });
                  const nextJsonString = produce(parseJsonString, (draft) => {
                    removeLosslessObject(draft);
                  });

                  const isValid = ajv.validate(nextJsonSchema, nextJsonString);

                  if (!isValid) {
                    setErrors([ajv.errorsText()]);
                    return;
                  }
                  onOk(jsonString);
                } catch (e) {
                  const aggregateAjvErrors = new AggregateAjvError(ajv.errors);
                  const messages = [];
                  for (const error of aggregateAjvErrors) {
                    messages.push(error.message);
                  }
                  setErrors(messages);

                  return;
                }
              }}
              size="small"
            >
              Save Edits
            </Button>
          </div>
        )
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          maxHeight: 'calc(100vh - 200px)',
          overflow: 'auto',
        }}
      >
        <JsonEditor
          data={jsonString}
          readOnly={readOnly}
          onJsonChange={(jsString) => setJsonString(jsString)}
        />
        {errors?.length > 0 && (
          <Alert
            style={{ flex: '0 1 auto', overflowY: 'auto', overflowX: 'hidden' }}
            message="Errors"
            description={<ErrorList errors={errors} />}
            type="error"
          />
        )}
      </div>
    </Modal>
  );
};

export default JsonEditorModal;
